import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { get_school_id } from './school.service';

/**
 * SSR-aware Cookie Service
 * 
 * This service provides a unified interface for reading/writing cookies
 * that works in both browser and server-side rendering contexts.
 * 
 * On the server, it reads cookies from the Express request object.
 * On the browser, it reads/writes cookies using document.cookie.
 */
@Injectable({ providedIn: 'root' })
export class SsrCookieService {
  private isBrowser: boolean;
  private cookieCache: Map<string, string> | null = null;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private request: Request
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    
    // On server, parse cookies once and cache them
    if (!this.isBrowser && this.request?.headers?.cookie) {
      this.cookieCache = this.parseCookies(this.request.headers.cookie);
    }
  }

  /**
   * Get the token cookie key for the current school
   */
  get tokenCookieKey(): string {
    return `online_shop_${get_school_id()}_token`;
  }

  /**
   * Get a cookie value by name
   */
  getCookie(name: string): string | null {
    if (this.isBrowser) {
      // Browser: read from document.cookie
      return this.getBrowserCookie(name);
    } else {
      // Server: read from cached parsed cookies
      return this.cookieCache?.get(name) || null;
    }
  }

  /**
   * Set a cookie (browser only - server cannot set cookies this way)
   * For SSR, the response headers must be set separately in server.ts
   */
  setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (!this.isBrowser) {
      return; // Cannot set cookies on server via this service
    }

    const {
      path = '/',
      domain,
      maxAge,
      expires,
      secure = false,
      sameSite = 'Lax',
      httpOnly = false // Note: httpOnly cannot be set via JavaScript
    } = options;

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    
    if (path) cookie += `; path=${path}`;
    if (domain) cookie += `; domain=${domain}`;
    if (maxAge !== undefined) cookie += `; max-age=${maxAge}`;
    if (expires) cookie += `; expires=${expires.toUTCString()}`;
    if (secure) cookie += '; secure';
    if (sameSite) cookie += `; samesite=${sameSite}`;

    this.document.cookie = cookie;
  }

  /**
   * Delete a cookie
   */
  deleteCookie(name: string, path: string = '/'): void {
    if (!this.isBrowser) return;
    
    // Set cookie with expired date to delete it
    this.document.cookie = `${encodeURIComponent(name)}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  /**
   * Get the auth token from cookie (works on both server and browser)
   */
  getAuthToken(): string | null {
    return this.getCookie(this.tokenCookieKey);
  }

  /**
   * Set the auth token cookie (browser only)
   * Call this when user logs in
   */
  setAuthToken(token: string, expiresInSeconds?: number): void {
    if (!this.isBrowser) return;

    const options: CookieOptions = {
      path: '/',
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:'
    };

    if (expiresInSeconds) {
      options.maxAge = expiresInSeconds;
    } else {
      // Default: 7 days
      options.maxAge = 7 * 24 * 60 * 60;
    }

    this.setCookie(this.tokenCookieKey, token, options);
  }

  /**
   * Remove the auth token cookie (browser only)
   * Call this when user logs out
   */
  removeAuthToken(): void {
    this.deleteCookie(this.tokenCookieKey);
  }

  /**
   * Check if we have an auth token (works on both server and browser)
   */
  hasAuthToken(): boolean {
    const token = this.getAuthToken();
    return !!token && token.length > 0;
  }

  /**
   * Parse cookie string into a Map
   */
  private parseCookies(cookieString: string): Map<string, string> {
    const cookies = new Map<string, string>();
    
    if (!cookieString) return cookies;

    cookieString.split(';').forEach(cookie => {
      const [rawName, ...rawValue] = cookie.split('=');
      if (rawName) {
        const name = decodeURIComponent(rawName.trim());
        const value = rawValue.join('=');
        if (value) {
          cookies.set(name, decodeURIComponent(value.trim()));
        }
      }
    });

    return cookies;
  }

  /**
   * Get cookie from browser document.cookie
   */
  private getBrowserCookie(name: string): string | null {
    const cookies = this.parseCookies(this.document.cookie);
    return cookies.get(name) || null;
  }
}

/**
 * Cookie options interface
 */
export interface CookieOptions {
  path?: string;
  domain?: string;
  maxAge?: number; // in seconds
  expires?: Date;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  httpOnly?: boolean;
}
